import React from 'react'
import { useForm, Link } from '@inertiajs/react'
import { KeyRound } from 'lucide-react'
import AuthLayout from '@/layouts/AuthLayout'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'

type Props = {
  reset_password_token: string
  errors?: Record<string, string[]>
}

export default function PasswordEdit({ reset_password_token, errors = {} }: Props) {
  const { data, setData, put, processing } = useForm({
    reset_password_token,
    password: '',
    password_confirmation: '',
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    put('/password')
  }

  return (
    <AuthLayout>
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            <KeyRound className="h-8 w-8 text-primary" />
          </div>
          <CardTitle className="text-xl">新しいパスワードを設定</CardTitle>
          <CardDescription>
            新しいパスワードを入力してください
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            {errors.reset_password_token && (
              <Alert variant="destructive">
                <AlertDescription>
                  リセットリンクが無効または期限切れです。
                  <Link href="/password/new" className="ml-1 underline">
                    再度リクエスト
                  </Link>
                  してください。
                </AlertDescription>
              </Alert>
            )}

            {errors.password && (
              <Alert variant="destructive">
                <AlertDescription>{errors.password[0]}</AlertDescription>
              </Alert>
            )}

            {errors.password_confirmation && (
              <Alert variant="destructive">
                <AlertDescription>{errors.password_confirmation[0]}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="password">新しいパスワード</Label>
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                value={data.password}
                onChange={(e) => setData('password', e.target.value)}
                minLength={12}
                required
              />
              <p className="text-xs text-muted-foreground">
                12文字以上、特殊文字を1文字以上含む
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password_confirmation">新しいパスワード（確認）</Label>
              <Input
                id="password_confirmation"
                type="password"
                autoComplete="new-password"
                value={data.password_confirmation}
                onChange={(e) => setData('password_confirmation', e.target.value)}
                minLength={12}
                required
              />
            </div>
          </CardContent>
          <CardFooter className="flex flex-col gap-4 pt-2">
            <Button type="submit" className="w-full" disabled={processing}>
              パスワードを変更
            </Button>
            <p className="text-sm text-muted-foreground">
              ログイン画面に戻る{' '}
              <Link href="/login" className="text-primary hover:underline">
                ログイン
              </Link>
            </p>
          </CardFooter>
        </form>
      </Card>
    </AuthLayout>
  )
}
